import React from "react";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Button,
  Badge,
  Flex,
} from "@chakra-ui/react";
import { SelectedSupplier, PartQuantity } from "./utils/partsCalculationUtils";

interface ManualSupplierListProps {
  suppliers: any[];
  selectedSuppliersInBucket: SelectedSupplier[];
  isOrderFullyFulfilled: boolean;
  onSelectSupplier: (supplier: any) => void;
}

const SupplierText: React.FC<{
  children: React.ReactNode;
  textStyle?: string;
}> = ({ children, textStyle = "body" }) => {
  const styles = {
    header: { fontSize: "lg", fontWeight: "bold", color: "gray.900" },
    label: { fontSize: "0.95rem", fontWeight: "700", color: "gray.600" },
    body: { fontSize: "0.85rem", fontWeight: "600", color: "gray.700" },
  };

  return (
    <Text {...(styles[textStyle as keyof typeof styles] || styles.body)}>
      {children}
    </Text>
  );
};

export const ManualSupplierList: React.FC<ManualSupplierListProps> = ({
  suppliers,
  selectedSuppliersInBucket,
  isOrderFullyFulfilled,
  onSelectSupplier,
}) => {
  if (suppliers.length === 0) {
    return (
      <Box
        bg="white"
        borderRadius="xl"
        p={8}
        textAlign="center"
        border="1px solid"
        borderColor="gray.200"
      >
        <Text color="gray.500" fontSize="lg" fontWeight="600">
          No suppliers found
        </Text>
        <Text color="gray.400" fontSize="sm" mt={2}>
          No suppliers available for the specified brands
        </Text>
      </Box>
    );
  }

  return (
    <Box w="100%" flex={3.5} justifySelf="center">
      <Box
        borderRadius="xl"
        overflowY="auto"
        maxH="70vh"
        boxShadow="md"
        sx={{
          "&::-webkit-scrollbar": { display: "none" },
          "-ms-overflow-style": "none",
          "scrollbar-width": "none",
        }}
      >
        <Table
          fontFamily="'Nunito','Inter', sans-serif"
          variant="simple"
          size="m"
          bg="white"
          boxShadow="sm"
          fontSize="0.85rem"
          fontWeight={700}
        >
          <Thead
            height={42}
            bg="gray.50"
            fontFamily="'Nunito','Inter', sans-serif"
            position="sticky"
            top={0}
            zIndex={1}
          >
            <Tr>
              <Th
                textTransform="none"
                color="gray.600"
                width="60%"
                fontFamily="'Nunito','Inter', sans-serif"
                letterSpacing={0}
              >
                <Text
                  fontSize="0.95rem"
                  fontWeight="700"
                  color="gray.600"
                  marginLeft={4}
                >
                  Supplier Name
                </Text>
              </Th>
              <Th
                textTransform="none"
                textAlign="center"
                color="gray.600"
                width="20%"
                fontFamily="'Nunito','Inter', sans-serif"
                letterSpacing={0}
              >
                <SupplierText textStyle="label">Status</SupplierText>
              </Th>
              <Th
                textTransform="none"
                textAlign="center"
                color="gray.600"
                width="20%"
                fontFamily="'Nunito','Inter', sans-serif"
                letterSpacing={0}
              >
                <SupplierText textStyle="label">Actions</SupplierText>
              </Th>
            </Tr>
          </Thead>
          <Tbody overflow={"auto"}>
            {suppliers.map((supplier) => {
              const isAlreadyInBucket = selectedSuppliersInBucket.some(
                (s) => s.supplierId === supplier._id
              );
              const isSupplierDisabled =
                isAlreadyInBucket || isOrderFullyFulfilled;

              return (
                <Tr
                  key={supplier._id}
                  role="group"
                  cursor={isSupplierDisabled ? "not-allowed" : "pointer"}
                  _hover={{
                    bg: isSupplierDisabled ? "transparent" : "gray.50",
                  }}
                  transition="all 0.2s"
                  opacity={isAlreadyInBucket ? 0.6 : 1}
                  bg={isAlreadyInBucket ? "green.50" : "white"}
                  onClick={() => {
                    if (!isSupplierDisabled) {
                      onSelectSupplier(supplier);
                    }
                  }}
                >
                  <Td px={4} py={3}>
                    <Flex direction="column" gap={0}>
                      <Text
                        fontWeight="700"
                        fontSize="0.9rem"
                        color="gray.800"
                        fontFamily="'Nunito','Inter', sans-serif"
                      >
                        {supplier.businessName}
                      </Text>
                    </Flex>
                  </Td>
                  <Td textAlign="center">
                    {isAlreadyInBucket ? (
                      <Badge
                        colorScheme="green"
                        fontSize="xs"
                        px={3}
                        py={1}
                        borderRadius="full"
                        fontWeight="600"
                      >
                        In Bucket
                      </Badge>
                    ) : (
                      <Badge
                        colorScheme="gray"
                        fontSize="xs"
                        px={3}
                        py={1}
                        borderRadius="full"
                        fontWeight="600"
                      >
                        Available
                      </Badge>
                    )}
                  </Td>
                  <Td textAlign="center">
                    <Button
                      size="sm"
                      colorScheme={isAlreadyInBucket ? "gray" : "blue"}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!isSupplierDisabled) {
                          onSelectSupplier(supplier);
                        }
                      }}
                      isDisabled={isSupplierDisabled}
                      fontFamily="'Nunito','Inter', sans-serif"
                      fontSize="0.85rem"
                      fontWeight="600"
                      bg="#4b85f2"
                      _hover={{ bg: "#3a6fd9" }}
                    >
                      {isAlreadyInBucket ? "Already Added" : "Add to Bucket"}
                    </Button>
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
};
